import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { env } from '@config/env';

/** Compare dotted numeric versions (e.g. "2026.0701.1"). Returns -1 | 0 | 1. */
function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

interface OtaManifest {
  version: string | null;
  url?: string;
  checksum?: string | null;
  min_native_version?: string;
}

const JUST_UPDATED_KEY = 'ota_just_updated_to';

let checking = false;

// ---- minimal, framework-free UI (runs at bootstrap, before React mounts) ----

function ensureOverlay(): { setPercent: (p: number) => void; setLabel: (t: string) => void; remove: () => void } {
  const id = 'ota-overlay';
  document.getElementById(id)?.remove();
  const el = document.createElement('div');
  el.id = id;
  el.setAttribute('style', [
    'position:fixed', 'inset:0', 'z-index:2147483647',
    'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center', 'gap:18px',
    'background:rgba(17,24,39,0.92)', 'color:#fff',
    'font-family:ui-sans-serif,system-ui,-apple-system,sans-serif', 'backdrop-filter:blur(2px)',
  ].join(';'));
  el.innerHTML = `
    <div style="width:44px;height:44px;border:4px solid rgba(255,255,255,0.25);border-top-color:#fff;border-radius:9999px;animation:ota-spin 0.9s linear infinite"></div>
    <div id="ota-label" style="font-size:15px;font-weight:600">Updating app…</div>
    <div style="width:220px;height:6px;background:rgba(255,255,255,0.2);border-radius:9999px;overflow:hidden">
      <div id="ota-bar" style="height:100%;width:0%;background:#fff;transition:width 0.15s ease"></div>
    </div>
    <div id="ota-pct" style="font-size:12px;opacity:0.8">0%</div>
    <style>@keyframes ota-spin{to{transform:rotate(360deg)}}</style>`;
  document.body.appendChild(el);
  return {
    setPercent: (p: number) => {
      const pct = Math.max(0, Math.min(100, Math.round(p)));
      (document.getElementById('ota-bar') as HTMLElement | null)?.style.setProperty('width', `${pct}%`);
      const t = document.getElementById('ota-pct');
      if (t) t.textContent = `${pct}%`;
    },
    setLabel: (text: string) => {
      const l = document.getElementById('ota-label');
      if (l) l.textContent = text;
    },
    remove: () => document.getElementById(id)?.remove(),
  };
}

function toast(text: string): void {
  const el = document.createElement('div');
  el.setAttribute('style', [
    'position:fixed', 'left:50%', 'bottom:32px', 'transform:translateX(-50%)', 'z-index:2147483647',
    'background:#111827', 'color:#fff', 'padding:10px 16px', 'border-radius:9999px',
    'font:600 13px ui-sans-serif,system-ui,sans-serif', 'box-shadow:0 6px 20px rgba(0,0,0,0.3)', 'opacity:0',
    'transition:opacity 0.25s ease',
  ].join(';'));
  el.textContent = text;
  document.body.appendChild(el);
  requestAnimationFrame(() => { el.style.opacity = '1'; });
  setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 300); }, 3200);
}

// ---- core check / apply ----

async function checkAndApply(): Promise<void> {
  if (checking) return;
  checking = true;
  try {
    const platform = Capacitor.getPlatform(); // 'ios' | 'android'
    const manifestUrl =
      `${env.coreApiUrl}/public/ota/manifest` +
      `?app_template_id=${encodeURIComponent(env.appTemplateId)}&platform=${encodeURIComponent(platform)}`;

    const res = await fetch(manifestUrl, { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const body = (await res.json().catch(() => null)) as { data?: OtaManifest } | null;
    const manifest = body?.data;
    if (!manifest?.version || !manifest.url) return; // nothing published

    const info = await App.getInfo(); // { version (marketing), build (CURRENT_PROJECT_VERSION) }
    const nativeBuild = info.build || info.version || '0.0.0';
    const minNative = manifest.min_native_version || '0.0.0';
    if (compareVersions(nativeBuild, minNative) < 0) {
      console.info(`[ota] bundle ${manifest.version} needs native >= ${minNative} (have ${nativeBuild}) — skipping`);
      return;
    }

    const current = await CapacitorUpdater.current();
    if (current?.bundle?.version === manifest.version) return; // already on the latest

    // Real-time download with a visible progress overlay, then apply immediately.
    const overlay = ensureOverlay();
    overlay.setLabel(`Updating to ${manifest.version}…`);
    const progress = await CapacitorUpdater.addListener('download', (s: { percent?: number }) => {
      if (typeof s.percent === 'number') overlay.setPercent(s.percent);
    });

    try {
      const bundle = await CapacitorUpdater.download({
        url: manifest.url,
        version: manifest.version,
        ...(manifest.checksum ? { checksum: manifest.checksum } : {}),
      });
      overlay.setPercent(100);
      overlay.setLabel('Applying update…');
      try { localStorage.setItem(JUST_UPDATED_KEY, manifest.version); } catch { /* noop */ }
      // set() swaps the served bundle and reloads the webview into the new version.
      await CapacitorUpdater.set({ id: bundle.id });
    } finally {
      await progress.remove().catch(() => undefined);
      // overlay is torn down by the reload; remove defensively if set() didn't reload.
      overlay.remove();
    }
  } catch (error) {
    document.getElementById('ota-overlay')?.remove();
    console.warn('[ota] live update check failed (non-fatal):', error);
  } finally {
    checking = false;
  }
}

/**
 * Self-hosted live web-bundle OTA (@capgo/capacitor-updater). NATIVE ONLY.
 *
 * On launch + on every foreground (resume) it polls core-api for the latest bundle for
 * THIS app's `app_template_id` + platform; if newer than the installed bundle AND the
 * native binary satisfies `min_native_version`, it downloads it with a live progress
 * overlay and applies it immediately (webview reload). notifyAppReady() on boot confirms
 * the bundle is healthy so CapGo won't auto-roll-back. Best-effort / non-blocking.
 *
 * Native-plugin/permission changes still ship through the store; `min_native_version`
 * keeps a web bundle off any binary too old to run it.
 */
export async function initLiveUpdates(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!env.appTemplateId) return; // OTA channel is keyed by the app's template

  try {
    await CapacitorUpdater.notifyAppReady();

    // If we just applied an OTA update on the previous run, confirm it visibly.
    try {
      const applied = localStorage.getItem(JUST_UPDATED_KEY);
      if (applied) {
        localStorage.removeItem(JUST_UPDATED_KEY);
        setTimeout(() => toast(`✅ Updated to ${applied}`), 600);
      }
    } catch { /* noop */ }

    // Re-check whenever the app returns to the foreground (lets a freshly published
    // bundle be picked up live, without a cold restart).
    void App.addListener('resume', () => { void checkAndApply(); });
  } catch (error) {
    console.warn('[ota] init failed (non-fatal):', error);
  }

  void checkAndApply();
}
